/**
 * ASCII Art Generator - 核心类型定义
 *
 * 包含所有渲染引擎、参数、状态相关的类型定义
 */

// ============================================
// 动画类型定义
// ============================================

/** 动画类型 */
export type AnimationType =
  | 'wave'      // 波浪偏移
  | 'pulse'     // 脉动效果
  | 'jitter'    // 随机抖动
  | 'drift'     // 缓慢漂移
  | 'breathe'   // 局部呼吸（亮度/透明度/大小）
  | 'blink'     // 闪烁效果
  | 'rainbow'   // 流动彩虹
  | 'loop'      // 循环路径移动
  | 'comet'     // 彗星拖尾
  | 'audio'     // 动感音乐（模拟频谱）
  | 'stream'    // 串流式字符
  | 'none'      // 无动画

// ============================================
// 风格类型定义
// ============================================

/** 支持的渲染风格 */
export type StyleType =
  | 'classic'      // 经典ASCII
  | 'braille'      // 盲文字符
  | 'halftone'     // 半调点阵
  | 'dither'       // 点阵抖动
  | 'dither2'      // 高级抖动
  | 'dotcross'     // 十字点阵
  | 'line'         // 线条艺术
  | 'particles'    // 粒子效果
  | 'claude'       // Claude Code风格
  | 'retro'        // 复古CRT
  | 'terminal'     // 终端风格

/** 风格显示名称映射 */
export const STYLE_LABELS: Record<StyleType, string> = {
  classic: 'Classic ASCII',
  braille: 'Braille',
  halftone: 'Halftone',
  dither: 'Dot Screen',
  dither2: 'Dither',
  dotcross: 'Dot Cross',
  line: 'Line',
  particles: 'Particles',
  claude: 'Claude Code',
  retro: 'Retro Art',
  terminal: 'Terminal',
}

/** 所有风格列表 */
export const STYLE_LIST: StyleType[] = [
  'classic', 'braille', 'halftone', 'dither', 'dither2',
  'dotcross', 'line', 'particles', 'claude', 'retro', 'terminal'
]

// ============================================
// 字符集类型定义
// ============================================

/** Classic ASCII 字符集类型 */
export type CharsetType =
  | 'standard'
  | 'blocks'
  | 'detailed'
  | 'minimal'
  | 'binary'
  | 'custom'
  | 'letters-alphabet'
  | 'letters-lowercase'
  | 'letters-mixed'
  | 'letters-symbols'
  | 'braille'

/** Braille 变体类型 */
export type BrailleVariant = 'standard' | 'sparse' | 'dense'

/** Terminal 字符集类型 */
export type TerminalCharset =
  | 'binary'
  | 'brackets'
  | 'symbols'
  | 'mixed'
  | 'matrix'

// ============================================
// 颜色与抖动类型定义
// ============================================

/** 颜色模式 */
export type ColorMode =
  | 'grayscale'  // 灰度
  | 'color'      // 全彩色
  | 'green'      // 绿色终端
  | 'amber'      // 琥珀终端
  | 'custom'     // 自定义颜色

/** 抖动算法类型 */
export type DitherType =
  | 'none'
  | 'floyd-steinberg'
  | 'bayer'
  | 'atkinson'

/** 半调形状类型 */
export type HalftoneShape =
  | 'circle'
  | 'square'
  | 'diamond'
  | 'pentagon'
  | 'hexagon'

// ============================================
// 复古预设类型定义
// ============================================

/** 复古双色调预设 */
export type RetroDuotone =
  | 'amber-classic'
  | 'cyan-night'
  | 'violet-haze'
  | 'lime-pulse'
  | 'mono-ice'

/** 双色调颜色配置 */
export interface DuotoneColors {
  low: { r: number; g: number; b: number }
  high: { r: number; g: number; b: number }
}

// ============================================
// 叠加效果类型定义
// ============================================

/** 叠加效果预设 */
export type OverlayPreset =
  | 'none'
  | 'noise'
  | 'waves'
  | 'intervals'
  | 'beam'
  | 'glitch'
  | 'crt'
  | 'matrix'

/** 特效方向 */
export type EffectDirection =
  | 'up'
  | 'down'
  | 'left'
  | 'right'
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-right'

/** 所有方向列表 */
export const DIRECTION_LIST: EffectDirection[] = [
  'up', 'down', 'left', 'right',
  'top-left', 'top-right', 'bottom-left', 'bottom-right'
]

// ============================================
// 鼠标交互类型定义
// ============================================

/** 鼠标交互模式 */
export type MouseInteractionMode = 'attract' | 'push'

// ============================================
// 输出与导出类型定义
// ============================================

/** 输出纵横比 */
export type OutputAspect =
  | 'source'
  | '16:9'
  | '4:3'
  | '1:1'
  | '3:4'
  | '9:16'

/** 导出图片格式 */
export type ExportImageFormat = 'png' | 'jpg' | 'webp'

/** 渲染字体 */
export type RenderFont =
  | 'helvetica'
  | 'inter'
  | 'poppins'
  | 'space-grotesk'
  | 'vt323'

// ============================================
// 渲染参数接口定义
// ============================================

/** 基础渲染参数 */
export interface BaseRenderSettings {
  style: StyleType
  fontSize: number
  charSpacing: number
  contrast: number
  brightness: number
  opacity: number
  invert: boolean
  backgroundColor: string
}

/** Classic ASCII 渲染参数 */
export interface ClassicRenderSettings extends BaseRenderSettings {
  style: 'classic'
  charset: CharsetType
  customCharset: string
  colorMode: ColorMode
  customColor: string
}

/** Braille 渲染参数 */
export interface BrailleRenderSettings extends BaseRenderSettings {
  style: 'braille'
  brailleVariant: BrailleVariant
  colorMode: ColorMode
}

/** Halftone 渲染参数 */
export interface HalftoneRenderSettings extends BaseRenderSettings {
  style: 'halftone'
  halftoneShape: HalftoneShape
  halftoneSize: number
  halftoneRotation: number
  colorMode: ColorMode
  ditherType: DitherType
  ditherStrength: number
}

/** Dither 渲染参数 */
export interface DitherRenderSettings extends BaseRenderSettings {
  style: 'dither' | 'dither2'
  ditherType: DitherType
  ditherStrength: number
  colorMode: ColorMode
  ditherColorNum: number

  // 动画参数
  ditherAnimationEnabled: boolean
  ditherWaveAmplitude: number
  ditherWaveFrequency: number
  ditherWaveSpeed: number
  ditherWaveColor: string

  // 鼠标交互
  ditherMouseInteractionEnabled: boolean
  ditherMouseRadius: number
  mouseInteractionMode: MouseInteractionMode
}

/** Retro 渲染参数 */
export interface RetroRenderSettings extends BaseRenderSettings {
  style: 'retro'
  retroDuotone: RetroDuotone
  retroNoise: number
}

/** Terminal 渲染参数 */
export interface TerminalRenderSettings extends BaseRenderSettings {
  style: 'terminal'
  terminalCharset: TerminalCharset
  colorMode: ColorMode
}

/** Line 渲染参数 */
export interface LineRenderSettings extends BaseRenderSettings {
  style: 'line'
  lineLength: number
  lineWidth: number
  lineThickness: number
  lineRotation: number
  colorMode: ColorMode
}

/** Particles 渲染参数 */
export interface ParticlesRenderSettings extends BaseRenderSettings {
  style: 'particles'
  particleDensity: number
  particleChar: string
  colorMode: ColorMode
}

/** Claude 渲染参数 */
export interface ClaudeRenderSettings extends BaseRenderSettings {
  style: 'claude'
  claudeDensity: number
  colorMode: ColorMode
}

/** DotCross 渲染参数 */
export interface DotCrossRenderSettings extends BaseRenderSettings {
  style: 'dotcross'
  colorMode: ColorMode
  ditherType: DitherType
}

/** 联合类型：所有渲染参数 */
export type RenderSettings =
  | ClassicRenderSettings
  | BrailleRenderSettings
  | HalftoneRenderSettings
  | DitherRenderSettings
  | RetroRenderSettings
  | TerminalRenderSettings
  | LineRenderSettings
  | ParticlesRenderSettings
  | ClaudeRenderSettings
  | DotCrossRenderSettings

// ============================================
// 完整设置接口（包含所有参数）
// ============================================

/** 完整渲染设置（用于状态管理） */
export interface FullRenderSettings {
  // 基础参数
  style: StyleType
  fontSize: number
  charSpacing: number
  spacing: number
  contrast: number
  brightness: number
  opacity: number
  invert: boolean
  backgroundColor: string

  // 字符集
  charset: CharsetType
  customCharset: string
  brailleVariant: BrailleVariant
  terminalCharset: TerminalCharset

  // 颜色
  colorMode: ColorMode
  customColor: string

  // 半调
  halftoneShape: HalftoneShape
  halftoneSize: number
  halftoneRotation: number

  // 抖动
  ditherType: DitherType
  ditherStrength: number

  // 复古
  retroDuotone: RetroDuotone
  retroNoise: number

  // 线条
  lineLength: number
  lineWidth: number
  lineThickness: number
  lineRotation: number

  // 粒子
  particleDensity: number
  particleChar: string

  // Claude
  claudeDensity: number

  // 动画
  animationType: AnimationType
  ditherAnimationEnabled: boolean
  ditherMouseInteractionEnabled: boolean
  ditherMouseRadius: number
  mouseInteractionMode: MouseInteractionMode
  ditherWaveAmplitude: number
  ditherWaveFrequency: number
  ditherWaveSpeed: number
  ditherWaveColor: string
  ditherColorNum: number
  dither2WaveColor: string
  dither2AnimationEnabled: boolean
  dither2MouseInteractionEnabled: boolean
  dither2MouseRadius: number
  dither2ColorNum: number
  dither2PixelSize: number
  dither2WaveAmplitude: number
  dither2WaveFrequency: number
  dither2WaveSpeed: number

  // 叠加效果
  overlayPreset: OverlayPreset
  overlayStrength: number
  noiseScale: number
  noiseSpeed: number
  noiseDirection: EffectDirection
  glitchDirection: EffectDirection
  crtDirection: EffectDirection
  matrixDirection: EffectDirection
  matrixScale: number
  matrixSpeed: number
  intervalSpacing: number
  intervalSpeed: number
  intervalWidth: number
  intervalDirection: EffectDirection
  beamDirection: EffectDirection

  // 视觉效果
  vignette: number
  borderGlow: number
  bgDither: number
  inverseDither: number

  // 输出
  outputAspect: OutputAspect
  renderFont: string

  // 鼠标交互通用
  hoverStrength: number
  mouseAreaSize: number
  mouseSpread: number
}

// ============================================
// 渲染结果接口
// ============================================

/** 渲染结果 */
export interface RenderResult {
  canvas: HTMLCanvasElement
  width: number
  height: number
  charWidth: number
  charHeight: number
}

// ============================================
// 引擎接口定义
// ============================================

/** 渲染引擎接口 */
export interface IRenderEngine {
  readonly name: StyleType

  /** 渲染图片 */
  render(
    image: HTMLImageElement | HTMLCanvasElement | ImageData,
    settings: FullRenderSettings,
    canvas: HTMLCanvasElement
  ): RenderResult

  /** 获取输出尺寸 */
  getOutputSize(
    imageWidth: number,
    imageHeight: number,
    settings: FullRenderSettings
  ): { width: number; height: number }
}

// ============================================
// 工具类型
// ============================================

/** RGB颜色 */
export interface RGB {
  r: number
  g: number
  b: number
}

/** 像素采样结果 */
export interface SampledPixel {
  brightness: number
  color: RGB
}

/** 动画帧数据 */
export interface AnimationFrame {
  timestamp: number
  deltaTime: number
  frameCount: number
}

/** 鼠标位置 */
export interface MousePosition {
  x: number
  y: number
  isInCanvas: boolean
}

/** 文件上传结果 */
export interface UploadResult {
  success: boolean
  file?: File
  dataUrl?: string
  error?: string
}